import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PatientAppointmentServiceItem, PatientMedicalNoteServiceItem, PatientServiceItem, PatientServiceItemType, PaymentTypeName, ServiceItemPaymentStatus } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useMedicalNoteDetailsByIdQuery, useUserDisplayNameQuery, useUserQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AppointmentState, ProviderMedicalRole } from '@uplift/types';
import React from 'react';


const useStyle = makeStyles()(theme => ({
  highlight: {
    color: theme.palette.secondary.main,
  }
}));


const AppointmentServiceLabel: React.VFC<{
  item: PatientAppointmentServiceItem;
}> = ({ item }) => {
  const l10n = useL10n();

  return <span>{l10n.resources.appointment.shortType[item.service.appointment.type].toLowerCase()}</span>;
};


const MedicalNoteServiceLabel: React.VFC<{
  item: PatientMedicalNoteServiceItem;
}> = ({ item }) => {
  const { data: noteDetails } = useMedicalNoteDetailsByIdQuery(item.id, { suspense: true });
  const { data: provider } = useUserQuery(noteDetails.providerId, { suspense: true });

  const label = React.useMemo(() => {
    if (provider.medicalRole === ProviderMedicalRole.therapist) {
      return 'therapy';
    }

    if (provider.medicalRole === ProviderMedicalRole.psychiatrist) {
      return 'psychiatry';
    }

    return 'medical note';
  }, [ provider ]);

  return <span>{label}</span>;
};


const ServiceLabel: React.VFC<{ item: PatientServiceItem }> = ({ item }) => {
  if (item.serviceType === PatientServiceItemType.appointment) {
    return <AppointmentServiceLabel item={item} />;
  }

  if (item.serviceType === PatientServiceItemType.medicalNote) {
    return <MedicalNoteServiceLabel item={item} />;
  }

  return null;
};


export type SessionDetailsDescriptionProps = {
  row: PatientServiceItem;
};


export const SessionDetailsDescription: React.FC<SessionDetailsDescriptionProps> = ({ row }) => {
  const { classes } = useStyle();
  const timezone = useTimezone();
  const { data: displayName } = useUserDisplayNameQuery(row.provider.id, { suspense: true });

  const details = React.useMemo(() => {
    const serviceTime = CalendarDateService.date(row.serviceDate).tz(timezone).format('MM/DD/YYYY [at] hh:mm A');

    if (row.serviceType === PatientServiceItemType.appointment) {
      if (row.service.appointment.state === AppointmentState.cancelled) {
        return (
          <Typography>
            Your <ServiceLabel item={row} /> scheduled for {serviceTime} with {displayName.fullLegalName} has been <strong className={classes.highlight}>canceled</strong>.
          </Typography>
        );
      }
    }

    if (row.payment.type === PaymentTypeName.cash) {
      return (
        <Typography>
          This <ServiceLabel item={row} /> session with {displayName.fullLegalName}, held on {serviceTime}, was billed as <strong className={classes.highlight}>self pay</strong>.
        </Typography>
      );
    } else if (row.payment.type === PaymentTypeName.insurance) {
      if (row.payment.status === ServiceItemPaymentStatus.processing) {
        if (row.payment.secondaryInsuranceName) {
          return (
            <Typography>
              Your <strong className={classes.highlight}>{row.payment.insuranceName} insurance is processing</strong> for the <ServiceLabel item={row} /> session on {serviceTime} with {displayName.fullLegalName}, with {row.payment.secondaryInsuranceName} as secondary insurance.
            </Typography>
          );
        }
        return (
          <Typography>
            Your <strong className={classes.highlight}>{row.payment.insuranceName} insurance is processing</strong> for the <ServiceLabel item={row} /> session on {serviceTime} with {displayName.fullLegalName}.
          </Typography>
        );
      } else {
        if (row.payment.secondaryInsuranceName) {
          return (
            <Typography>
              <strong className={classes.highlight}>{row.payment.insuranceName} insurance was applied</strong> to the <ServiceLabel item={row} /> session held on {serviceTime} with {displayName.fullLegalName}, with {row.payment.secondaryInsuranceName} as secondary insurance.
            </Typography>
          );
        }
        return (
          <Typography>
            <strong className={classes.highlight}>{row.payment.insuranceName} insurance was applied</strong> to the <ServiceLabel item={row} /> session held on {serviceTime} with {displayName.fullLegalName}.
          </Typography>
        );
      }
    }

    return null;
  }, [ row, displayName, timezone, classes ]);

  if (!details) {
    return null;
  }

  return (
    <Box px={2}>{details}</Box>
  );
};
